import React from 'react';
import Axios from 'axios';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as AppRoutes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import SPAPageHeader from '../shared/SPAPageHeader';
import DevDocs from './DevDocs';
import SystemStatus from '../SystemStatus/SystemStatus';
import DevDashboard from './DevDashboard';
import AISafetyReport from './AISafety/AISafetyReport';
import AIPolicyView from '../AIPolicyView';
import ContentAuditTool from './ContentAudit/ContentAuditTool';
import type { DocFile } from './devDocs.utils';

interface DevDocsRouteProps {
  error: boolean;
  files?: DocFile[];
  isLoading: boolean;
}

// Only the DevDocs route depends on the manifest — other dev tools must not block on it.
const DevDocsRoute = ({ error, files, isLoading }: DevDocsRouteProps) => {
  if (!isLoading && !error && files) return <DevDocs basePath="/dev/dev-docs" files={files} />;

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader title="DevDocs" />
        {isLoading ? (
          <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Loading dev tools...</p>
        ) : (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error">Failed to load dev tools.</p>
        )}
      </div>
    </div>
  );
};

// AIDEV-NOTE: SPA-specific dev view that renders routes inline.
// Files are always fetched async from /api/v2/dev_docs via React Query.
const DevView = () => {
  const navigate = useNavigate();

  const { data: files, error, isLoading } = useQuery({
    queryFn: () => Axios.get(
      AppRoutes.plato_api_dev_docs_path(),
    ).then(r => r.data.data),
    queryKey: [QueryKeys.DevDocsManifest],
  });

  const handleNavigate = (path: string) => navigate(`/dev${path}`);

  return (
    <Routes>
      <Route
        element={(
          <DevDashboard onNavigate={handleNavigate} />
        )}
        path="/"
      />
      <Route
        element={<DevDocsRoute error={Boolean(error)} files={files} isLoading={isLoading} />}
        path="/dev-docs/*"
      />
      <Route element={<ContentAuditTool />} path="/content-audit" />
      <Route element={<AISafetyReport />} path="/ai-safety" />
      <Route element={<AIPolicyView />} path="/ai-policy" />
      <Route element={<SystemStatus />} path="/system-status" />
    </Routes>
  );
};

export default DevView;
